// src/components/GroupAssignmentPicker.jsx
import React, { useState } from 'react';
import { useT } from '../i18n';
import './GroupAssignmentPicker.css';

const messages = {
  search: { tr: 'Grup adı ara', en: 'Search group name' },
  empty: { tr: 'Görünür grup bulunamadı.', en: 'No visible groups found.' },
  noMatch: { tr: 'Aramanızla eşleşen grup bulunamadı.', en: 'No groups match your search.' },
  selectedOnly: { tr: 'Sadece seçilenler', en: 'Selected only' },
  selectedCount: { tr: '{n} / {total} seçili', en: '{n} / {total} selected' },
  priority: { tr: 'Öncelik', en: 'Priority' },
  priorityTitle: { tr: 'Öncelik değeri (1,2,3…)', en: 'Priority value (1, 2, 3…)' },
};

// Shared group-picker UI (search + "sadece seçilenler" toggle + checkbox
// grid), extracted from SignatureForm.jsx where it originally lived
// alongside a per-group priority input. Used as-is (showPriority=false) by
// Disclaimers.jsx/Campaigns.jsx, which only need a plain multi-select — and
// with showPriority by SignatureForm.jsx, which still needs to arbitrate
// conflicts when a user belongs to multiple assigned groups. Also used for
// the per-user picker ("Kişiye Özel Atama"): items then carry a `hint`
// (the e-mail) shown under the name and matched by the search too.
export default function GroupAssignmentPicker({
  groups = [],              // [{id, name, hint?}]
  assignments = {},         // {[group_id]: priority} — priority ignored when showPriority=false
  onToggleGroup,            // (id) => void
  onChangePriority,         // (id, value) => void — only used when showPriority
  showPriority = false,
  searchPlaceholder,
  emptyText,
  noMatchText,
}) {
  const { t, lang } = useT(messages);
  const [groupSearch, setGroupSearch] = useState('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const visibleGroups = groups.filter(g => {
    if (showSelectedOnly && assignments[g.id] == null) return false;
    if (groupSearch) {
      const q = groupSearch.toLocaleLowerCase(lang);
      const haystack = `${g.name} ${g.hint || ''}`.toLocaleLowerCase(lang);
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
  const selectedCount = Object.keys(assignments).length;

  return (
    <div className="group-assignment-picker">
      {groups.length > 0 && (
        <div className="group-assignment-toolbar">
          <div className="group-assignment-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" />
            </svg>
            <input
              type="text"
              className="group-assignment-search-field"
              placeholder={searchPlaceholder || t('search')}
              value={groupSearch}
              onChange={e => setGroupSearch(e.target.value)}
            />
          </div>
          <label className="group-assignment-selected-toggle">
            <input
              type="checkbox"
              checked={showSelectedOnly}
              onChange={e => setShowSelectedOnly(e.target.checked)}
            />
            <span className="group-assignment-checkbox-visual" aria-hidden="true">✓</span>
            {t('selectedOnly')}
          </label>
          <span className="group-assignment-count">{t('selectedCount', { n: selectedCount, total: groups.length })}</span>
        </div>
      )}

      <div className="group-assignment-list">
        {groups.length === 0 && (
          <p className="group-assignment-empty">{emptyText || t('empty')}</p>
        )}
        {groups.length > 0 && visibleGroups.length === 0 && (
          <p className="group-assignment-empty">{noMatchText || t('noMatch')}</p>
        )}
        {visibleGroups.map(g => {
          const prio = assignments[g.id];
          const checked = prio != null;
          return (
            <div key={g.id} className={`group-assignment-row${checked ? ' is-checked' : ''}`}>
              <label className="group-assignment-check">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggleGroup(g.id)}
                />
                <span className="group-assignment-checkbox-visual" aria-hidden="true">✓</span>
                {g.hint ? (
                  <span className="group-assignment-name-stack" title={`${g.name} — ${g.hint}`}>
                    <span className="group-assignment-name">{g.name}</span>
                    <span className="group-assignment-hint">{g.hint}</span>
                  </span>
                ) : (
                  <span className="group-assignment-name" title={g.name}>{g.name}</span>
                )}
              </label>
              {showPriority && checked && (
                <div className="group-assignment-priority">
                  <span className="group-assignment-priority-label">{t('priority')}</span>
                  <input
                    type="number"
                    min="1"
                    value={prio}
                    onChange={e => onChangePriority(g.id, e.target.value)}
                    className="priority-input"
                    title={t('priorityTitle')}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
